import { FreestyleRoom, FreestyleScore } from './types';
import { isLocalBackend, isNativeApp, phpAuthHeaders, REMOTE_API_BASE } from './apiEnv';

// 本地开发走 Node 的 /api 路径；网页部署用相对地址以兼容 /myapp 子路径；
// 原生 App 的页面来自 Capacitor 内置 localhost，必须换成线上绝对地址，
// 否则会打到 App 自己身上（isLocalBackend() 已经处理了这个区别）。
const makeUrl = (path: string) => {
    if (isLocalBackend()) return `/api/fs/rooms${path}`;
    const base = isNativeApp() ? `${REMOTE_API_BASE}/api.php` : './api.php';
    return `${base}/fs/rooms${path}?action=fs_rooms`;
};

// 原生 App 跨域请求不带 Cookie，统一补 X-Kendama-Auth 鉴权头。
const fsFetch = (path: string, init: RequestInit = {}, fresh = false): Promise<Response> => {
    const url = fresh ? makeFreshUrl(path) : makeUrl(path);
    return fetch(url, {
        credentials: 'include',
        cache: fresh ? 'no-store' : undefined,
        ...init,
        headers: phpAuthHeaders((init.headers as Record<string, string>) || undefined)
    });
};

const makeFreshUrl = (path: string) => {
    const url = new URL(makeUrl(path), window.location.href);
    url.searchParams.set('_ts', Date.now().toString());
    return url.toString();
};

const createApiError = (message: string, status?: number) => {
    const error = new Error(message) as Error & { status?: number };
    if (typeof status === 'number') {
        error.status = status;
    }
    return error;
};

export const fetchRooms = async () => {
    const res = await fsFetch('', {}, true);
    if (!res.ok) throw new Error('Failed to fetch rooms');
    return res.json();
};

export const createRoom = async (name: string, password?: string) => {
    const res = await fsFetch('', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, password })
    });
    if (!res.ok) throw new Error('Failed to create room');
    return res.json();
};

export const joinRoom = async (roomId: string, password?: string) => {
    const res = await fsFetch(`/${roomId}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
    });
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to join room');
    }
    return res.json();
};

export const fetchRoom = async (roomId: string) => {
    const res = await fsFetch(`/${roomId}`, {}, true);
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw createApiError(data.error || 'Failed to fetch room', res.status);
    }
    return res.json();
};

export const updateRoom = async (roomId: string, room: Partial<FreestyleRoom>) => {
    const res = await fsFetch(`/${roomId}/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ room })
    });
    if (!res.ok) throw new Error('Failed to update room');
    return res.json();
};

export const submitScore = async (roomId: string, competitorId: string, score: FreestyleScore) => {
    const res = await fsFetch(`/${roomId}/score`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ competitorId, score })
    });
    if (!res.ok) throw new Error('Failed to submit score');
    return res.json();
};

export const submitScoresBatch = async (
    roomId: string,
    scores: Array<{ competitorId: string; score: FreestyleScore }>
) => {
    const res = await fsFetch(`/${roomId}/score-batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scores })
    });
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to submit scores');
    }
    return res.json();
};

export const calculatePrelimResults = async (roomId: string) => {
    const res = await fsFetch(`/${roomId}/calculate-prelims`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
    });
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to calculate prelim results');
    }
    return res.json();
};

export const submitVote = async (roomId: string, matchId: string, votedCompetitorId: string) => {
    const res = await fsFetch(`/${roomId}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ matchId, votedCompetitorId })
    });
    if (!res.ok) throw new Error('Failed to submit vote');
    return res.json();
};

export const deleteRoom = async (roomId: string) => {
    const res = await fsFetch(`/${roomId}/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
    });
    if (!res.ok) throw new Error('Failed to delete room');
    return res.json();
};
